import React, { useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { typography } from '../../utils/theme';
import { PaletteIcon, GlobeIcon, EyeIcon, ZapIcon, CheckIcon, TypeIcon, Volume2Icon, DownloadIcon, UploadIcon, RotateCcwIcon, InfoIcon } from 'lucide-react';
export function SettingsApp() {
  const {
    getThemeClasses,
    theme,
    setTheme,
    language,
    setLanguage,
    reduceMotion,
    setReduceMotion,
    highContrast,
    setHighContrast,
    t
  } = useTheme();
  const themeClasses = getThemeClasses();
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [animationSpeed, setAnimationSpeed] = useState<'slow' | 'normal' | 'fast'>('normal');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const themes = [{
    id: 'dark',
    label: t.settings?.themes?.dark || 'Donker',
    icon: '🌙',
    color: 'from-slate-700 to-slate-900'
  }, {
    id: 'light',
    label: t.settings?.themes?.light || 'Licht',
    icon: '☀️',
    color: 'from-blue-400 to-indigo-500'
  }, {
    id: 'neon',
    label: t.settings?.themes?.neon || 'Neon',
    icon: '✨',
    color: 'from-cyan-500 to-pink-500'
  }];
  const languages = [{
    id: 'nl',
    label: 'Nederlands',
    flag: '🇳🇱'
  }, {
    id: 'en',
    label: 'English',
    flag: '🇬🇧'
  }];
  const fontSizes = [{
    id: 'small',
    label: 'Klein',
    size: 'text-sm'
  }, {
    id: 'medium',
    label: 'Normaal',
    size: 'text-base'
  }, {
    id: 'large',
    label: 'Groot',
    size: 'text-lg'
  }];
  const animationSpeeds = [{
    id: 'slow',
    label: 'Langzaam',
    speed: '0.8x'
  }, {
    id: 'normal',
    label: 'Normaal',
    speed: '1.0x'
  }, {
    id: 'fast',
    label: 'Snel',
    speed: '1.5x'
  }];
  const handleExportSettings = () => {
    const settings = {
      theme,
      language,
      reduceMotion,
      highContrast,
      fontSize,
      animationSpeed,
      soundEnabled
    };
    const blob = new Blob([JSON.stringify(settings, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mvl-settings.json';
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleResetSettings = () => {
    setTheme('dark');
    setLanguage('nl');
    setReduceMotion(false);
    setHighContrast(false);
    setFontSize('medium');
    setAnimationSpeed('normal');
    setSoundEnabled(false);
  };
  const settingsCards = [{
    id: 'theme',
    title: t.settings?.theme || 'Thema',
    icon: PaletteIcon,
    color: 'from-purple-500 to-pink-500',
    rotation: -2
  }, {
    id: 'language',
    title: t.settings?.language || 'Taal',
    icon: GlobeIcon,
    color: 'from-blue-500 to-cyan-500',
    rotation: 1
  }, {
    id: 'accessibility',
    title: t.settings?.accessibility || 'Toegankelijkheid',
    icon: EyeIcon,
    color: 'from-emerald-500 to-teal-500',
    rotation: -1
  }, {
    id: 'display',
    title: 'Weergave',
    icon: TypeIcon,
    color: 'from-orange-500 to-amber-500',
    rotation: 2
  }, {
    id: 'advanced',
    title: 'Geavanceerd',
    icon: InfoIcon,
    color: 'from-indigo-500 to-purple-500',
    rotation: -3
  }];
  return <div className={`p-4 md:p-6 lg:p-8 ${themeClasses.text} max-w-6xl mx-auto`} data-id="element-513">
      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-8" data-id="element-514">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3" data-id="element-515">
          {t.settings?.title || 'Instellingen'}
        </h1>
        <p className={`text-base md:text-lg ${themeClasses.text} opacity-70 max-w-3xl`} data-id="element-516">
          Personaliseer je ervaring naar jouw voorkeur
        </p>
      </motion.div>
      <div className="space-y-8" data-id="element-517">
        {/* Theme selection - Pinboard style */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.1
      }} className="relative" data-id="element-518">
          <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-6`} style={{
          boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
          transform: 'rotate(-0.5deg)'
        }} data-id="element-519">
            <div className="flex items-center gap-3 mb-4" data-id="element-520">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center" data-id="element-521">
                <PaletteIcon className="w-5 h-5 text-white" data-id="element-522" />
              </div>
              <h3 className={`${typography.h4}`} data-id="element-523">
                {t.settings?.theme || 'Thema'}
              </h3>
            </div>
            <div className="grid grid-cols-3 gap-3" data-id="element-524">
              {themes.map(themeOption => <button key={themeOption.id} onClick={() => setTheme(themeOption.id as any)} className={`p-4 rounded-lg border-2 transition-all ${theme === themeOption.id ? `border-purple-500 bg-gradient-to-br ${themeOption.color} text-white` : `border-transparent ${themeClasses.card}`}`} data-id="element-525">
                  <div className="text-3xl mb-2" data-id="element-526">{themeOption.icon}</div>
                  <p className="text-sm font-medium" data-id="element-527">{themeOption.label}</p>
                  {theme === themeOption.id && <CheckIcon className="w-4 h-4 text-white mx-auto mt-2" data-id="element-528" />}
                </button>)}
            </div>
          </div>
          <div className="absolute -top-3 left-8 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg" data-id="element-529" />
        </motion.div>
        {/* Language selection - Pinboard style */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2
      }} className="relative" data-id="element-530">
          <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-6`} style={{
          boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
          transform: 'rotate(0.8deg)'
        }} data-id="element-531">
            <div className="flex items-center gap-3 mb-4" data-id="element-532">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center" data-id="element-533">
                <GlobeIcon className="w-5 h-5 text-white" data-id="element-534" />
              </div>
              <h3 className={`${typography.h4}`} data-id="element-535">
                {t.settings?.language || 'Taal'}
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-536">
              {languages.map(lang => <button key={lang.id} onClick={() => setLanguage(lang.id as any)} className={`p-4 rounded-lg border-2 transition-all ${language === lang.id ? 'border-blue-500 bg-blue-500/10' : `border-transparent ${themeClasses.card}`}`} data-id="element-537">
                  <div className="text-2xl mb-2" data-id="element-538">{lang.flag}</div>
                  <p className="text-sm font-medium" data-id="element-539">{lang.label}</p>
                  {language === lang.id && <CheckIcon className="w-4 h-4 text-blue-500 mx-auto mt-2" data-id="element-540" />}
                </button>)}
            </div>
          </div>
          <div className="absolute -top-3 right-12 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg" data-id="element-541" />
        </motion.div>
        {/* Accessibility - Pinboard style */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3
      }} className="relative" data-id="element-542">
          <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-6`} style={{
          boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
          transform: 'rotate(-0.3deg)'
        }} data-id="element-543">
            <div className="flex items-center gap-3 mb-4" data-id="element-544">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center" data-id="element-545">
                <EyeIcon className="w-5 h-5 text-white" data-id="element-546" />
              </div>
              <h3 className={`${typography.h4}`} data-id="element-547">
                {t.settings?.accessibility || 'Toegankelijkheid'}
              </h3>
            </div>
            <div className="space-y-3" data-id="element-548">
              <button onClick={() => setReduceMotion(!reduceMotion)} className={`w-full flex items-center justify-between p-4 rounded-lg ${themeClasses.card} border ${themeClasses.border} hover:scale-[1.01] transition-all`} data-id="element-549">
                <div className="flex items-center gap-3" data-id="element-550">
                  <ZapIcon className="w-5 h-5" data-id="element-551" />
                  <div className="text-left" data-id="element-552">
                    <p className="font-medium" data-id="element-553">
                      {t.settings?.reduceMotion || 'Verminder Beweging'}
                    </p>
                    <p className="text-sm opacity-60" data-id="element-554">
                      Minder animaties en transities
                    </p>
                  </div>
                </div>
                <div className={`w-12 h-6 rounded-full transition-colors ${reduceMotion ? 'bg-emerald-500' : 'bg-gray-600'}`} data-id="element-555">
                  <motion.div className="w-5 h-5 bg-white rounded-full m-0.5" animate={{
                  x: reduceMotion ? 24 : 0
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-556" />
                </div>
              </button>
              <button onClick={() => setHighContrast(!highContrast)} className={`w-full flex items-center justify-between p-4 rounded-lg ${themeClasses.card} border ${themeClasses.border} hover:scale-[1.01] transition-all`} data-id="element-557">
                <div className="flex items-center gap-3" data-id="element-558">
                  <EyeIcon className="w-5 h-5" data-id="element-559" />
                  <div className="text-left" data-id="element-560">
                    <p className="font-medium" data-id="element-561">
                      {t.settings?.highContrast || 'Hoog Contrast'}
                    </p>
                    <p className="text-sm opacity-60" data-id="element-562">
                      Betere leesbaarheid en scherpere randen
                    </p>
                  </div>
                </div>
                <div className={`w-12 h-6 rounded-full transition-colors ${highContrast ? 'bg-emerald-500' : 'bg-gray-600'}`} data-id="element-563">
                  <motion.div className="w-5 h-5 bg-white rounded-full m-0.5" animate={{
                  x: highContrast ? 24 : 0
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-564" />
                </div>
              </button>
            </div>
          </div>
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg" data-id="element-565" />
        </motion.div>
        {/* Display settings - Pinboard style */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.4
      }} className="relative" data-id="element-566">
          <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-6`} style={{
          boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
          transform: 'rotate(0.5deg)'
        }} data-id="element-567">
            <div className="flex items-center gap-3 mb-4" data-id="element-568">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center" data-id="element-569">
                <TypeIcon className="w-5 h-5 text-white" data-id="element-570" />
              </div>
              <h3 className={`${typography.h4}`} data-id="element-571">Weergave</h3>
            </div>
            <div className="space-y-4" data-id="element-572">
              {/* Font size */}
              <div data-id="element-573">
                <p className="text-sm font-medium mb-2 opacity-70" data-id="element-574">
                  Tekstgrootte
                </p>
                <div className="grid grid-cols-3 gap-2" data-id="element-575">
                  {fontSizes.map(size => <button key={size.id} onClick={() => setFontSize(size.id as any)} className={`p-3 rounded-lg border-2 transition-all ${fontSize === size.id ? 'border-orange-500 bg-orange-500/10' : `border-transparent ${themeClasses.card}`}`} data-id="element-576">
                      <p className={`font-medium ${size.size}`} data-id="element-577">{size.label}</p>
                    </button>)}
                </div>
              </div>
              {/* Animation speed */}
              <div data-id="element-578">
                <p className="text-sm font-medium mb-2 opacity-70" data-id="element-579">
                  Animatiesnelheid
                </p>
                <div className="grid grid-cols-3 gap-2" data-id="element-580">
                  {animationSpeeds.map(speed => <button key={speed.id} onClick={() => setAnimationSpeed(speed.id as any)} className={`p-3 rounded-lg border-2 transition-all ${animationSpeed === speed.id ? 'border-orange-500 bg-orange-500/10' : `border-transparent ${themeClasses.card}`}`} data-id="element-581">
                      <p className="font-medium text-sm" data-id="element-582">{speed.label}</p>
                      <p className="text-xs opacity-60" data-id="element-583">{speed.speed}</p>
                    </button>)}
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -top-3 right-8 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg" data-id="element-584" />
        </motion.div>
        {/* Advanced settings - Pinboard style */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.5
      }} className="relative" data-id="element-585">
          <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-6`} style={{
          boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
          transform: 'rotate(-0.8deg)'
        }} data-id="element-586">
            <div className="flex items-center gap-3 mb-4" data-id="element-587">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center" data-id="element-588">
                <InfoIcon className="w-5 h-5 text-white" data-id="element-589" />
              </div>
              <h3 className={`${typography.h4}`} data-id="element-590">Geavanceerd</h3>
            </div>
            <div className="space-y-3" data-id="element-591">
              {/* Sound effects */}
              <button onClick={() => setSoundEnabled(!soundEnabled)} className={`w-full flex items-center justify-between p-4 rounded-lg ${themeClasses.card} border ${themeClasses.border} hover:scale-[1.01] transition-all`} data-id="element-592">
                <div className="flex items-center gap-3" data-id="element-593">
                  <Volume2Icon className="w-5 h-5" data-id="element-594" />
                  <div className="text-left" data-id="element-595">
                    <p className="font-medium" data-id="element-596">Geluidseffecten</p>
                    <p className="text-sm opacity-60" data-id="element-597">
                      Subtiele feedback geluiden
                    </p>
                  </div>
                </div>
                <div className={`w-12 h-6 rounded-full transition-colors ${soundEnabled ? 'bg-indigo-500' : 'bg-gray-600'}`} data-id="element-598">
                  <motion.div className="w-5 h-5 bg-white rounded-full m-0.5" animate={{
                  x: soundEnabled ? 24 : 0
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-599" />
                </div>
              </button>
              {/* Export/Import buttons */}
              <div className="grid grid-cols-2 gap-3 pt-2" data-id="element-600">
                <button onClick={handleExportSettings} className={`flex items-center justify-center gap-2 p-3 rounded-lg ${themeClasses.card} border ${themeClasses.border} hover:scale-[1.02] transition-all`} data-id="element-601">
                  <DownloadIcon className="w-4 h-4" data-id="element-602" />
                  <span className="text-sm font-medium" data-id="element-603">Exporteer</span>
                </button>
                <button className={`flex items-center justify-center gap-2 p-3 rounded-lg ${themeClasses.card} border ${themeClasses.border} hover:scale-[1.02] transition-all opacity-50 cursor-not-allowed`} disabled data-id="element-604">
                  <UploadIcon className="w-4 h-4" data-id="element-605" />
                  <span className="text-sm font-medium" data-id="element-606">Importeer</span>
                </button>
              </div>
              {/* Reset button */}
              <button onClick={handleResetSettings} className={`w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-red-500/30 hover:border-red-500 hover:bg-red-500/10 transition-all`} data-id="element-607">
                <RotateCcwIcon className="w-4 h-4 text-red-500" data-id="element-608" />
                <span className="text-sm font-medium text-red-500" data-id="element-609">
                  Reset naar standaard
                </span>
              </button>
            </div>
          </div>
          <div className="absolute -top-3 left-16 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg" data-id="element-610" />
        </motion.div>
        {/* Info footer */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} className={`text-center text-sm ${themeClasses.text} opacity-50 py-4`} data-id="element-611">
          <p data-id="element-612">
            Instellingen worden lokaal opgeslagen in je browser • Versie 2024.1
          </p>
        </motion.div>
      </div>
    </div>;
}